/* eslint-disable jsx-a11y/anchor-has-content */
/* eslint-disable jsx-a11y/heading-has-content */

import { useLocation, useNavigate, useParams } from "react-router-dom";
import { RelatedArticlesCarousel } from "../components/relatedArticlesCarousel";
import WebsiteFooter from "common/layout/websiteFooter";
import { useEffect, useRef, useState } from "react";
import { Articles } from "../../data/models/article";
import Markdown from "react-markdown";
import useGetArticles from "../hooks/useGetArticles";
import { SocialShare } from "../components/socialShare";
import { SeoProvider } from "features/components/utils/seoContext";
import { SeoMetaDecorator } from "features/components/utils/seoMetaDecorator";
import { useGetArticle } from "../hooks/useGetArticle";
import KroLoadingIndicator, {
  LoaderType,
} from "features/components/loader/kroCircularIndicator";

import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import KroBlogShareModal from "features/components/modals/kroBlogShareModal";
import KroBackButton from "features/components/buttons/kroBackButton";
import AuthorCard from "../components/authorCard";

export function BlogPostPage() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const stateArticle = state as Articles;
  const [readTime, setReadTime] = useState(0);
  const { slug } = useParams<{ slug: string }>();
  const { data: articles } = useGetArticles();
  const { data: articleData, isLoading: isLoadingArticle } = useGetArticle(
    slug ?? "",
  );

  // Use fetched article data if available, otherwise fall back to state data
  const article = articleData?.data[0] || stateArticle;

  console.log(article?.content);

  // Refs for content
  const contentRef = useRef<HTMLDivElement>(null);

  // Calculate read time
  useEffect(() => {
    if (article?.content) {
      // Average reading speed (words per minute)
      const wordsPerMinute = 200;

      // Count words in content
      const wordCount = article.content.split(/\s+/).length;

      // Calculate read time in minutes
      const minutes = Math.ceil(wordCount / wordsPerMinute);
      setReadTime(minutes);
    }
  }, [article]);

  // Show loading state if we're loading and don't have state data
  if (isLoadingArticle && !stateArticle) {
    return <KroLoadingIndicator type={LoaderType.FIXED} />;
  }

  // Show error state if we have no article data
  if (!article) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold">Article not found</h1>
          <p className="mt-2">The article you're looking for doesn't exist.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <KroBlogShareModal
        slug={article?.slug ?? ""}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
      />
      <SeoProvider
        value={{
          title: article?.seo?.metaTitle || "",
          description: article?.seo?.metaDescription || "",
          url: window.location.href,
          shareImage: article?.seo?.sharedImage?.data?.attributes?.url
            ? `${process.env.REACT_APP_STRAPI_URL}${article.seo.sharedImage.data.attributes.url}`
            : "",
          keywords: article?.seo?.keywords?.replaceAll(",", " ") || "",
          preventIndexing: article?.seo?.preventIndexing || false,
        }}
      >
        <SeoMetaDecorator />
        <div className="relative mx-[16px] mt-[40px] flex flex-grow flex-col lg:mt-[80px] lg:gap-[64px]">
          <div className="flex flex-row items-center justify-center lg:mx-[100px]">
            <p className="text-center font-inter text-[32px] font-semibold capitalize leading-[100%] text-[#131313] lg:font-dm-sans lg:text-[72px] lg:tracking-[-0.03em]">
              {article?.title}
            </p>
          </div>
          <div className="sticky top-0 z-30 hidden items-center justify-around bg-white transition-shadow duration-200 lg:flex lg:flex-row lg:shadow-sm">
            <div className="flex w-full items-center justify-around py-4">
              <KroBackButton
                onclick={() => navigate(-1)}
                title="Back to main blog"
              />
              <p className="font-inter text-[14px] font-semibold leading-[100%] tracking-[-1.5%] text-[#131313]">
                By {article?.author}
              </p>
              <SocialShare onClick={() => setIsOpen(true)} />
              <div className="flex items-center justify-center rounded-[33.9px] border-[0.45px] border-[#D2D2D2] px-[12px] py-[8px]">
                <div className="flex items-center">
                  <img src="/assets/icons/time.svg" alt="time" />
                  <p className="ml-[6px] text-[14px] font-medium leading-[100%] tracking-[0%] text-[#131313]">
                    {readTime} min read
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="hidden rounded-[12px] lg:flex">
            <img
              src={`${process.env.REACT_APP_STRAPI_URL}${article.cover?.formats?.large?.url}`}
              alt={"blog post cover"}
              className="mx-[100px] max-h-[463px] w-full rounded-[12px] object-cover"
            />
          </div>
          <div className="flex rounded-[12px] lg:hidden">
            <img
              src={`${process.env.REACT_APP_STRAPI_URL}${article.cover?.formats?.large?.url}`}
              alt={"blog post cover"}
              className="mb-6 mt-8 max-h-[463px] w-full rounded-[12px] object-cover"
            />
          </div>

          <div className="sticky top-0 z-30 flex flex-col lg:hidden">
            <div className="flex flex-row justify-between">
              <KroBackButton
                onclick={() => navigate(-1)}
                title="Back to main blog"
              />
              <SocialShare
                onClick={() => setIsOpen(true)}
                className="order-2"
              />
            </div>
            <div className="flex flex-row items-center justify-between">
              <p className="order-3 font-inter text-[14px] font-semibold leading-[100%] tracking-[-1.5%] text-[#131313]">
                By {article?.author}
              </p>
              <div className="flex items-center justify-center rounded-[33.9px] border-[0.45px] border-[#D2D2D2] px-[12px] py-[8px]">
                <div className="flex items-center">
                  <img src="/assets/icons/time.svg" alt="time" />
                  <p className="ml-[6px] text-[14px] font-medium leading-[100%] tracking-[0%] text-[#131313]">
                    {readTime} min read
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-6 border-b-[1px] border-[#D2D2D2] lg:hidden"></div>
        </div>

        <div className="mx-[16px] flex flex-col lg:mx-[75px] lg:flex-row lg:gap-[32px]">
          <div ref={contentRef} className="w-full flex-1">
            <div className="mt-[32px] flex flex-col gap-[16px] lg:mt-0 lg:flex-row lg:items-start">
              <div className="flex-1"></div>
            </div>
            <div className="flex flex-col gap-[16px] lg:items-start">
              <div className="prose prose-lg mx-auto max-w-none overflow-hidden lg:mt-8 lg:max-w-[870px]">
                <Markdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeRaw]}
                  components={{
                    blockquote: ({ node, ...props }) => (
                      <blockquote
                        className="my-4 border-l-4 border-[#022928] pl-4 italic"
                        {...props}
                      />
                    ),
                    p: ({ node, ...props }) => (
                      <p className="my-4" {...props} />
                    ),
                    h1: ({ node, ...props }) => (
                      <h1 className="my-4 text-[30px] font-bold" {...props} />
                    ),
                    h2: ({ node, ...props }) => (
                      <h2 className="my-4 text-[24px] font-bold" {...props} />
                    ),
                    h3: ({ node, ...props }) => (
                      <h3 className="my-4 text-[20px] font-bold" {...props} />
                    ),
                    h4: ({ node, ...props }) => (
                      <h4 className="my-4 text-[18px] font-bold" {...props} />
                    ),
                    h5: ({ node, ...props }) => (
                      <h5 className="my-4 text-[16px] font-bold" {...props} />
                    ),
                    h6: ({ node, ...props }) => (
                      <h6 className="my-4 text-[14px] font-bold" {...props} />
                    ),
                    hr: ({ node, ...props }) => (
                      <hr className="my-4" {...props} />
                    ),
                    br: ({ node, ...props }) => (
                      <br className="my-4" {...props} />
                    ),
                    strong: ({ node, ...props }) => (
                      <strong className="my-4" {...props} />
                    ),
                    em: ({ node, ...props }) => (
                      <em className="my-4" {...props} />
                    ),
                    ul: ({ node, ...props }) => (
                      <ul className="my-4 list-disc pl-6" {...props} />
                    ),
                    ol: ({ node, ...props }) => (
                      <ol className="my-4 list-decimal pl-6" {...props} />
                    ),
                    li: ({ node, ...props }) => (
                      <li className="my-2" {...props} />
                    ),
                    a: ({ node, ...props }) => (
                      <a
                        className="text-[#022928] underline hover:text-[#022928]/80"
                        target="_blank"
                        rel="noopener noreferrer"
                        {...props}
                      />
                    ),
                    img: ({ node, ...props }) => (
                      <img
                        className="my-4 rounded-[10px]"
                        src={node?.properties?.src as string}
                        alt={node?.properties?.alt as string}
                        {...props}
                      />
                    ),
                    span: ({ node, ...props }) => (
                      <span className="my-4" {...props} />
                    ),
                  }}
                >
                  {article?.content || ""}
                </Markdown>
              </div>
            </div>
          </div>
        </div>
        <AuthorCard article={article} />

        <div className="mx-[16px] mt-[56px] lg:mx-[75px] lg:mt-[128px]">
          <RelatedArticlesCarousel
            allArticles={articles?.pages.flatMap((page) => page.data) || []}
            currentArticle={article}
          />
        </div>
        <div className="mt-[80px] lg:mt-[128px]">
          <WebsiteFooter />
        </div>
      </SeoProvider>
    </>
  );
}
